/*
 * Message templates: the text a church writes once, with the parts that change marked.
 *
 * The rule that shapes all of this: **a member must never receive `{{first_name}}`.** A template rendered with a
 * missing parameter is not a cosmetic problem, it is a message that tells a congregation their church's software is
 * broken. So rendering reports what is missing rather than quietly substituting an empty string, and the editor is
 * expected to refuse the send rather than to hope.
 */

import { measure, type Measurement } from "./segments";

/** `{{name}}`: two braces, a name of letters, digits and underscores, two braces. Nothing cleverer. */
const PARAMETER = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;

/** The longest a template body may be. Taken from what messaging providers accept, and shorter than any of them. */
export const MAX_TEMPLATE_BODY = 1024;

/** Every parameter the template uses, in the order it first uses them, without repeats. */
export function parametersUsed(body: string): string[] {
  const found: string[] = [];
  for (const match of body.matchAll(PARAMETER)) {
    const name = match[1];
    if (name !== undefined && !found.includes(name)) found.push(name);
  }
  return found;
}

export interface RenderResult {
  readonly text: string;
  /** Parameters the template used that the caller did not supply. A send with any of these must be refused. */
  readonly missing: readonly string[];
  /** Parameters supplied that the template never uses. Harmless, but usually a typo in one of the two. */
  readonly unused: readonly string[];
  readonly measurement: Measurement;
}

/**
 * Renders a template.
 *
 * A missing parameter is **left in place**, exactly as written, rather than replaced with a blank. Two reasons: the
 * preview then shows the church what a member would receive if it were sent, which is the argument for fixing it;
 * and a blank silently produces "Hi , see you Sunday", which reads as a different kind of broken and is easier to
 * miss in a list of a hundred.
 */
export function render(
  body: string,
  parameters: Readonly<Record<string, string>> = {},
): RenderResult {
  const used = parametersUsed(body);
  const missing = used.filter((name) => parameters[name] === undefined);
  const unused = Object.keys(parameters).filter((name) => !used.includes(name));

  const text = body.replace(PARAMETER, (whole, name: string) => parameters[name] ?? whole);

  return { text, missing, unused, measurement: measure(text) };
}

export interface TemplateProblem {
  readonly kind: "too-long" | "empty" | "unknown-parameter";
  readonly message: string;
}

/**
 * What is wrong with a template as written, before anyone tries to send it.
 *
 * `known` is the set of parameters the platform can actually supply. A template that asks for one nobody can fill is
 * broken at write time, and saying so then is far cheaper than discovering it against a live audience.
 */
export function checkTemplate(body: string, known: readonly string[] = []): TemplateProblem[] {
  const problems: TemplateProblem[] = [];
  if (body.trim().length === 0) {
    problems.push({ kind: "empty", message: "A template needs something to say." });
  }
  if (body.length > MAX_TEMPLATE_BODY) {
    problems.push({
      kind: "too-long",
      message: `A template is at most ${String(MAX_TEMPLATE_BODY)} characters; this one is ${String(body.length)}.`,
    });
  }
  if (known.length > 0) {
    for (const name of parametersUsed(body)) {
      if (!known.includes(name)) {
        problems.push({
          kind: "unknown-parameter",
          message: `Nothing can fill {{${name}}}. Available: ${known.join(", ")}.`,
        });
      }
    }
  }
  return problems;
}
